import { defineComponent, reactive, SetupContext, ref } from '@vue/composition-api';
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';              // フィルタ用共通コンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import { ImportantInformationReceiveList, TableHeader, ColumnMenu, PullDown } from "@/shared/type";
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';
import vueRouter from '@/router/index';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';
import { DataOptions } from 'vuetify';
import CommonFunction from '@/shared/common-function';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                      // Windowサイズ
    tableHeight: number;                                                       // テーブルの高さ
    filterDefs: TableFilterDefs;                                               // フィルタ定義
    filters: TableFilters;                                                     // フィルタ情報
    receiveListDisplayTableHeaders: TableHeader[];                             // 受信情報一覧ヘッダ情報
    receiveListItem: ImportantInformationReceiveList[];                        // 受信情報一覧情報
    pageOptions: DataOptions;                                                  // ページ情報を保存する変数
    isLoading: boolean;                                                        // テーブルがロード中: true ロード中でない: false
    total: number;                                                             // 受信情報一覧の総件数
    receiveInfoDeleteDialog: ColumnMenu;                                       // 受信情報削除ダイアログ
    visibleColumnMenu: ColumnMenu;                                             // 列メニューの表示有無
    address: string;                                                           // 選択中の宛先区分
    addressPulldown: PullDown[];                                               // プルダウンの値
    isFromDetail: boolean;                                                     // 詳細画面からの遷移フラグ
}

/**
 * 重要情報のお知らせ：受信情報一覧画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonTableFilter },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                              // ルータ
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;                  // 1ページあたりの行数候補（10, 50, 100）
        const pageNation = ref<number>(1);                                     // ページ番号（状態管理用）
        const itemsPerPage = ref<number>(linesPerPage[0]);                     // 1ページあたりの行数（状態管理用、デフォルト値：10）
        const sortBy = ref<string[]>([]);                                      // ソートカラム（状態管理用）
        const sortDesc = ref<boolean[]>([]);                                   // ソート順（状態管理用）
        let isPageLastData = false;                                            // ページの最終データフラグ
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;               // テーブルのデータ取得中に表示するテキスト
        const filterRef = ref<any>(null);                                      // フィルタコンポーネント
        const commonRef = ref<any>(null);                                      // メッセージダイアログ
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                        // ウィンドウサイズ
            tableHeight: SystemConst.TABLE_HEIGHT.IMPORTANT_INFORMATION_RECEIVE_LIST,
            filterDefs: {                                                      // フィルタ定義
                'freeWord': { title: 'フリーワード', types: 'FreeWord' },
                'sendOrg': { title: '発信組織', types: 'PullDown' },
                'receivingDate': { title: '受信日', types: 'DatePeriod' }
            },
            filters: {},                                                       // フィルタ情報
            receiveListDisplayTableHeaders: [],                                // 受信情報一覧ヘッダー
            receiveListItem: [],                                               // 受信情報一覧情報
            pageOptions: {                                                     // ページ情報を保存する変数
                page: 1, itemsPerPage: linesPerPage[0], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            isLoading: false,                                                  // テーブルがロード中: true ロード中でない: false
            total: 0,                                                          // 受信情報一覧の総件数
            receiveInfoDeleteDialog: {},                                       // 受信情報削除ダイアログの表示有無
            visibleColumnMenu: {},                                             // 列メニューの表示有無
            address: '',                                                       // プルダウン選択中の値
            addressPulldown: [],                                               // プルダウンの値
            isFromDetail: false                                                // 詳細画面からの遷移フラグ
        });
        const tableHeaderClass: string[] = ['tableHeader subtitle-2'];         // 受信情報一覧のヘッダ色
        // 受信情報一覧ヘッダ情報(初期表示ヘッダ)
        const receiveListInitialDisplayTableHeaders: TableHeader[] = [
            {
                text: '',
                value: 'isNew',
                sortable: false,
                width: '4%',
                class: tableHeaderClass
            },
            {
                text: 'タイトル',
                value: 'title',
                width: '42%',
                class: tableHeaderClass
            },
            {
                text: '発信組織',
                value: 'sendOrg',
                width: '15%',
                class: tableHeaderClass
            },
            {
                text: '発信者',
                value: 'sender',
                width: '15%',
                class: tableHeaderClass
            },
            {
                text: '受信日',
                value: 'receivingDate',
                width: '15%',
                class: tableHeaderClass
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        // フィルタバリデーション
        // フリーワード
        const freeWordRules = [
            (v: any) => CommonFunction.checkLength(
                v,
                SystemConst.IMPORTANT_INFORMATION_LIST_MAX_LENGTH.FREE_WORD,
                'フリーワード'
            )
        ];

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '受信情報一覧');

            // storeから前回のフィルタ条件を復元
            state.filters = ctx.root.$store.state.importantInformation.filtersReceive;

            // storeから前回のページ番号、1ページあたりの行数を復元
            pageNation.value = ctx.root.$store.state.importantInformation.pageNationReceive;
            itemsPerPage.value = ctx.root.$store.state.importantInformation.itemsPerPageReceive;

            // storeから前回のソートキー、ソート順を復元
            sortBy.value = ctx.root.$store.state.importantInformation.sortByReceive;
            sortDesc.value = ctx.root.$store.state.importantInformation.sortDescReceive;

            // storeから前回のプルダウン選択の値を復元
            state.address = ctx.root.$store.state.importantInformation.pulldownReceive;

            // storeから詳細画面からの遷移フラグを復元
            state.isFromDetail = ctx.root.$store.state.importantInformation.isFromDetailReceive;

            // プルダウン情報を初期設定
            // awaitで処理の完了を待つ
            await getAddress();

            // 初期表示ヘッダ設定
            state.receiveListDisplayTableHeaders = receiveListInitialDisplayTableHeaders;

            // vuexに保持されているフィルタ情報を復元する
            // 前回のフィルタに対するID格納用の配列
            const filterIdList = [];
            // 前回のフィルタ条件格納用の配列
            const filterParameters = [];

            // state.filtersのプロパティ(key)で以下を繰り返し、有効なフィルタだけを抽出
            for (const key in state.filters) {
                // state.filtersのプロパティにkeyがある場合
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    // 有効なフィルタである場合
                    if (val.isEnabled) {
                        // フィルタのID(key)とフィルタ条件を保存
                        filterIdList.push(key);
                        filterParameters.push(val.condition);
                    }
                }
            }

            // 復元された有効なフィルタがある場合
            if (filterIdList.length !== 0) {
                // setAllFiltersで有効なフィルタをすべてstateに設定
                filterRef.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // vuexに保持されているフィルタ情報を復元しなかった場合
                // 受信情報一覧情報取得関数呼び出し
                getList();
            }

            // 詳細画面からの遷移フラグにfalseを設定
            // trueのままだと通常のフィルタリング時に1ページ目へ戻らなくなるため
            state.isFromDetail = false;
        }

        /**
         * 受信情報一覧情報取得
         */
        async function getList() {
            // テーブルローダーのフラグをtrueにする
            state.isLoading = true;

            // ページの最終データフラグをfalseにリセット
            isPageLastData = false;

            // storeにフィルタ、ページ、ソート情報、プルダウン選択の値を保存する
            ctx.root.$store.commit('importantInformation/setPageNationReceive', pageNation.value);
            ctx.root.$store.commit('importantInformation/setItemsPerPageReceive', itemsPerPage.value);
            ctx.root.$store.commit('importantInformation/setFiltersReceive', state.filters);
            ctx.root.$store.commit('importantInformation/setSortByReceive', sortBy.value);
            ctx.root.$store.commit('importantInformation/setSortDescReceive', sortDesc.value);
            ctx.root.$store.commit('importantInformation/setPulldownReceive', state.address);

            // フィルタ、ページ、ソート情報から共通関数 makeListParameters()を呼び出し、APIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // 受信一覧取得のAPI通信をする
            // パラメータ：ページ番号
            //             1ページの行数
            //             ソートキー
            //             ソート順
            //             フィルタ情報
            //             プルダウン選択の値
            const response = await ctx.root.$store.dispatch('importantInformation/getReceivesInformation',
                {
                    page: params['page'],
                    itemsPerPage: params['itemsPerPage'],
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams'],
                    address: state.address
                }
            );

            // 受信一覧の取得に失敗した場合
            if (response.status !== HttpStatus.OK) {
                // テーブルがロード中のフラグをfalseに設定する
                state.isLoading = false;

                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 受信したデータをテーブルに格納する
            state.receiveListItem = response.data.importantReceives;
            // 総データ件数を格納する
            state.total = response.data.totalCount;
            
            // 取得件数が1件かつ総データ件数が1件より多い場合、isPageLastDataをtrueにする
            if (state.receiveListItem.length === 1 && state.total > 1) {
                isPageLastData = true;
            }
            
            // テーブルローダーのフラグをfalseにする
            state.isLoading = false;
        }

        /**
         * プルダウン情報取得・設定
         */
        async function getAddress() {
            // 受信一覧プルダウン情報APIでプルダウンの値を取得
            // GET /api/important-information/receive/addresses
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('importantInformation/getReceivePulldown');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // レスポンスデータが2件以上ある場合
            if (response.data.addressPulldown.length >= 2) {
                // レスポンスデータを格納
                state.addressPulldown = response.data.addressPulldown;

                // state.tableHeightの値を変更する
                state.tableHeight = SystemConst.TABLE_HEIGHT.IMPORTANT_INFORMATION_RECEIVE_LIST_ADDRESS;
            }

            // storeから前回のプルダウン選択の値が復元されていない場合
            if (state.address === '') {
                // レスポンスデータの最初の1件目のvalueをstate.addressに格納する
                state.address = response.data.addressPulldown[0].value;
            }
            
            // フィルタ情報を設定
            await initFilter();
        }

        /**
         * プルダウンの値が変更された際のイベント
         */
        function changeAddress() {
            // ページを1ページ目に戻す
            pageNation.value = 1;
            state.pageOptions.page = 1;

            // フィルタ条件clear
            // ※ state.filtersを初期化した際に、updateFilters()が起動し、一覧を更新する
            state.filters = {};
            // 表示されているフィルタ条件を削除する
            filterRef.value.allClear();

            // 発信組織のフィルタプルダウン再取得
            initFilter();
        }

        /**
         * フィルタ情報を取得・設定
         */
        async function initFilter() {
            // 受信一覧フィルタ情報APIでフィルタに表示する情報を取得
            // GET /api/important-information/receive/filter-conditions
            // パラメータ：宛先プルダウンで選択されたID
            const response = await ctx.root.$store.dispatch('importantInformation/getReceiveFilter', {
                receiveAddress: state.address
            });

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 以下の項目にAPIで取得したフィルタ情報を設定
            // 発信組織
            state.filterDefs['sendOrg'].items = response.data.sendOrganizations;

            // 以下の項目にフィルタバリデーションを設定
            // フリーワード
            state.filterDefs['freeWord'].validator = freeWordRules;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる
         * このイベントでv-data-tableの表示更新を行うこと
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            // filtersをstateへ保存する
            state.filters = filters;

            // 詳細画面からの遷移でない場合
            if (!state.isFromDetail) {
                // 1ページへ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // 受信一覧再取得処理
            getList();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * 
         * ページ操作とソート操作のタイミングで呼ばれる
         * このイベントでv-data-tableの表示更新を行うこと
         * 
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            // ページ/ソート情報のデータの中身を比較して、いずれも変更されていなかった場合
            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // 処理を終了する
                return;
            }

            // ページ/ソート情報を画面に適用する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;

            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;

            // 受信一覧再取得処理
            getList();
        }

        /**
         * 重要情報のお知らせ詳細画面へ遷移する
         * 
         * @param importantId 重要情報ID
         * @param sequence 送付シーケンス
         */
        function navigationDetail(importantId: string, sequence: string) {
            // storeの詳細画面からの遷移フラグをtrueに設定
            ctx.root.$store.commit('importantInformation/setIsFromDetailReceive', true);

            // 引数をパラメータとして重要情報のお知らせ詳細画面へ遷移する
            router.push('/important-information/detail/' + importantId + '/' + sequence + '/receive');
        }

        /**
         * 重要情報のお知らせの受信データを削除する
         * 
         * @param importantId 重要情報ID
         * @param sequence 送付シーケンス
         */
        async function receiveDelete(importantId: string, sequence: string) {
            // 受信一覧削除APIを呼ぶ
            //   DELETE /api/important-information/receive/{importantId}/{sendSequence}
            //   パラメータ：重要情報ID, 送付シーケンス
            const response = await ctx.root.$store.dispatch('importantInformation/deleteReceiveInformation',
                {
                    importantId,
                    sendSequence: sequence
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示して、処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 削除完了のダイアログを表示する
            commonRef.value.displayMessageDialog(response);

            // isPageLastDataがtrueの場合、ページ番号を-1する
            if (isPageLastData) {
                pageNation.value -= 1;
                state.pageOptions.page -= 1;
            }

            // 受信一覧の表示を更新する
            getList();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            linesPerPage,
            pageNation,
            itemsPerPage,
            sortBy,
            sortDesc,
            tableLoadingText,
            commonRef,
            filterRef,
            changeAddress,
            updateFilter,
            updateOptions,
            navigationDetail,
            receiveDelete,
            onResize
        };
    }
});
